import { useEffect, useState } from "react";

import PageHeader from "../components/PageHeader.jsx";
import apiClient from "../services/api.js";

export default function KycPage() {
  const [documentType, setDocumentType] = useState("National ID");
  const [file, setFile] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadSubmissions = async () => {
    const { data } = await apiClient.get("/kyc/me");
    setSubmissions(data || []);
  };

  useEffect(() => {
    loadSubmissions().catch(() => {});
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("");
    setError("");
    if (!file) {
      setError("Please upload a document");
      return;
    }
    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("document_type", documentType);
      formData.append("document", file);
      await apiClient.post("/kyc/", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setStatus("KYC submission uploaded.");
      setFile(null);
      await loadSubmissions();
    } catch (err) {
      setError(err?.response?.data?.message || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title="KYC documents"
        description="Upload identity documents for compliance review."
      />
      <form onSubmit={handleSubmit} className="card-surface rounded-3xl p-6 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
              Document type
            </label>
            <select
              value={documentType}
              onChange={(event) => setDocumentType(event.target.value)}
              className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
            >
              <option>National ID</option>
              <option>Driver's License</option>
              <option>Passport</option>
              <option>Proof of Address</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
              Upload file
            </label>
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
            />
          </div>
        </div>
        {error && <p className="text-sm text-rose-600">{error}</p>}
        {status && <p className="text-sm text-emerald-600">{status}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-2xl bg-accent px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "Uploading..." : "Submit KYC"}
        </button>
      </form>

      <section className="card-surface rounded-3xl p-6">
        <h2 className="text-xl font-semibold text-accent">Submissions</h2>
        <div className="mt-4 space-y-3">
          {submissions.map((submission) => (
            <div
              key={submission.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/70 px-4 py-3"
            >
              <div>
                <p className="text-sm font-semibold text-ink">{submission.document_type}</p>
                <p className="text-xs text-slate-500">{submission.document_filename}</p>
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-500">
                {submission.status}
              </div>
            </div>
          ))}
          {submissions.length === 0 && (
            <p className="text-sm text-slate-500">No submissions yet.</p>
          )}
        </div>
      </section>
    </div>
  );
}
